import React, { useState } from 'react';
import HamburgerMenu from '../global/menu';
import MayoLogo from '../assets/images/Mayo.png';
import ExpirationModal from '../components/ExpirationModal';
import TruckCheckModal from '../components/TruckCheckModal';
import { useExpirationCheck } from '../hooks/useExpirationCheck';
import '../global/styles.css';

// Sleek hazard badge icon
const HazardBadge = ({ status, onClick }) => {
  if (status === 'ok' || status === 'no-data' || status === 'error') return null;

  const isCritical = status === 'critical';
  const color = isCritical ? '#dc3545' : '#ffc107';

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={isCritical ? 'hazard-badge-critical' : 'hazard-badge-warning'}
      style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        background: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: `0 2px 8px ${color}60`,
        transition: 'all 0.2s ease',
        zIndex: 10
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.15)';
        e.currentTarget.style.boxShadow = `0 4px 12px ${color}80`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = `0 2px 8px ${color}60`;
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </div>
  );
};

function DashboardHome({ userInfo, isAdmin = false, onLogout, onNavigate }) {
  const [showEMSModal, setShowEMSModal] = useState(false);
  const [showTruckModal, setShowTruckModal] = useState(false);
  const [expirationModalData, setExpirationModalData] = useState(null);
  const { expirationData } = useExpirationCheck();

  const locations = [
    { id: 'mayo-21', name: 'Mayo 21', path: '/locations/mayo21', expirationId: 'mayo21' },
    { id: 'mayo-22', name: 'Mayo 22', path: '/locations/mayo22', expirationId: 'mayo22' },
    { id: 'mayo-23', name: 'Mayo 23', path: '/locations/mayo23', expirationId: 'mayo23' },
    { id: 'mayo-24', name: 'Mayo 24', path: '/locations/mayo24', expirationId: 'mayo24' },
    { id: 'mayo-25', name: 'Mayo 25', path: '/locations/mayo25', expirationId: 'mayo25' },
    { id: 'ems-station', name: 'EMS Station', path: '/locations/ems-captain', hasOptions: true, expirationId: 'emsstation' },
    { id: 'daily-truck', name: 'Daily Truck Check', path: '/locations/engine41' }
  ];

  const handleLocationClick = (location) => {
    if (location.id === 'ems-station') {
      setShowEMSModal(true);
    } else if (location.id === 'daily-truck') {
      setShowTruckModal(true);
    } else {
      onNavigate(location.id);
    }
  };

  const handleHazardClick = (location) => {
    const data = expirationData[location.expirationId];
    if (data && data.medications && data.medications.length > 0) {
      setExpirationModalData(data);
    }
  };

  const getExpirationStatus = (location) => {
    if (!location.expirationId) return 'ok';
    const data = expirationData[location.expirationId];
    return data?.status || 'ok';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '20px',
      paddingTop: 'max(20px, env(safe-area-inset-top))',
      paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
      paddingLeft: 'max(20px, env(safe-area-inset-left))',
      paddingRight: 'max(20px, env(safe-area-inset-right))'
    }}>
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: '20px' }}>

        {/* Header - Clean and Centered */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: window.innerWidth <= 768 ? '30px' : '40px'
        }}>
          <img
            src={MayoLogo}
            alt="Mayo Clinic"
            style={{
              width: window.innerWidth <= 768 ? '100px' : '120px',
              height: window.innerWidth <= 768 ? '100px' : '120px',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Title */}
        <h1 style={{
          textAlign: 'center',
          color: '#666',
          fontSize: window.innerWidth <= 768 ? '20px' : '24px',
          fontWeight: '400',
          marginBottom: window.innerWidth <= 768 ? '30px' : '50px',
          letterSpacing: '1px',
          padding: '0 10px'
        }}>
          Log And Manage Your Inventory
        </h1>

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768
            ? '1fr'
            : 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: window.innerWidth <= 768 ? '16px' : '25px',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {locations.map(location => (
            <div
              key={location.id}
              style={{
                position: 'relative',
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: window.innerWidth <= 768 ? '12px' : '15px',
                padding: window.innerWidth <= 768 ? '24px 20px' : '30px 25px',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                minHeight: window.innerWidth <= 768 ? '140px' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (window.innerWidth > 768) {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (window.innerWidth > 768) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {/* Hazard Badge */}
              <HazardBadge
                status={getExpirationStatus(location)}
                onClick={() => handleHazardClick(location)}
              />

              <h3 style={{
                color: '#333',
                fontSize: window.innerWidth <= 768 ? '17px' : '18px',
                fontWeight: '600',
                marginBottom: window.innerWidth <= 768 ? '16px' : '20px',
                letterSpacing: '1px'
              }}>
                {location.name}
              </h3>
              <button
                className="btn btn-primary btn-full"
                onClick={() => handleLocationClick(location)}
                style={{
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  padding: window.innerWidth <= 768 ? '12px 20px' : '14px 24px',
                  minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                }}
              >
                UPDATE INVENTORY
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* EMS Station Options Modal */}
      {showEMSModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: window.innerWidth <= 768 ? '20px' : '0'
        }}>
          <div style={{
            background: 'white',
            borderRadius: window.innerWidth <= 768 ? '16px' : '15px',
            padding: window.innerWidth <= 768 ? '24px' : '40px',
            maxWidth: '500px',
            width: window.innerWidth <= 768 ? '100%' : '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: window.innerWidth <= 768 ? '24px' : '30px'
            }}>
              <h2 style={{
                color: '#333',
                fontSize: window.innerWidth <= 768 ? '20px' : '24px',
                margin: 0
              }}>
                EMS Station Options
              </h2>
              <button
                onClick={() => setShowEMSModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px',
                  minWidth: '44px',
                  minHeight: '44px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                className="btn btn-primary btn-full"
                onClick={() => {
                  setShowEMSModal(false);
                  onNavigate('ems-station');
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '16px' : '20px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                  background: '#000',
                  borderColor: '#000',
                  minHeight: '44px'
                }}
              >
                Update Station Inventory
              </button>

              <button
                className="btn btn-primary btn-full"
                onClick={() => {
                  setShowEMSModal(false);
                  onNavigate('ems-expired');
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '16px' : '20px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                  background: '#0E3293',
                  borderColor: '#0E3293',
                  minHeight: '44px'
                }}
              >
                >
                Update Expired Medications
              </button>

              <button
                className="btn btn-primary btn-full"
                onClick={() => {
                  setShowEMSModal(false);
                  onNavigate('non-controlled');
                }}
                style={{
                  padding: window.innerWidth <= 768 ? '16px' : '20px',
                  fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                  background: '#2e7d32',
                  borderColor: '#2e7d32',
                  minHeight: '44px'
                }}
              >
                Non-Controlled Medications
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Truck Check Modal */}
      {showTruckModal && (
        <TruckCheckModal
          onClose={() => setShowTruckModal(false)}
          onCheckIn={() => {
            setShowTruckModal(false);
            onNavigate('daily-truck');
          }}
          onCheckOut={() => {
            setShowTruckModal(false);
            onNavigate('truck-checkout');
          }}
        />
      )}

      {/* Expiration Modal */}
      {expirationModalData && (
        <ExpirationModal
          locationData={expirationModalData}
          onClose={() => setExpirationModalData(null)}
        />
      )}
    </div>
  );
}

export default DashboardHome;
